import { CircleCheck, CircleX, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  type LoaderFunctionArgs,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { Button, Card, CardContent, CardFooter } from '@virtual-care/shadcn';

import { ProfileCard } from '../../components/header';
import Header from '../../components/header/header';
import { homecareReservationStore } from '../../hooks/reservation/homecare.ts';

export function homecareRequestStatusLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  if (
    url.searchParams.get('ok') === '1' &&
    homecareReservationStore.read() !== null
  ) {
    homecareReservationStore.write(null);
  }
  return null;
}

export default function HomecareRequestStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const paymentStatus = searchParams.get('ok');

  const isPaymentSuccess = paymentStatus === '1';
  const isPaymentFailure = paymentStatus === '0';

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-chart-3 rounded-full p-2">
              <Home className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('homecare.pageTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>

      <div className="flex h-[460px] items-center justify-center">
        <div className="card-container">
          <Card className="card">
            <CardContent className="flex flex-col gap-5">
              <div className="flex flex-col items-center justify-center gap-4">
                {isPaymentFailure && (
                  <>
                    <CircleX
                      className="h-24 w-24 text-red-500"
                      strokeWidth={2}
                    />
                    <h2 className="text-center text-3xl font-bold text-gray-900">
                      {t('booking.requestStatus.paymentFailed')}
                    </h2>
                    <p className="max-w-md text-center text-base text-gray-500">
                      {t('booking.requestStatus.paymentFailedDescription')}
                    </p>
                  </>
                )}

                {isPaymentSuccess && (
                  <>
                    <CircleCheck
                      className="text-primary h-24 w-24"
                      strokeWidth={2}
                    />
                    <h2 className="text-center text-3xl font-bold text-gray-900">
                      {t('booking.requestStatus.underReview')}
                    </h2>
                    <p className="max-w-md text-center text-base text-gray-500">
                      {t(
                        'booking.requestStatus.underReviewHomecareDescription',
                      )}
                    </p>
                  </>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex w-full justify-center gap-4">
              {isPaymentFailure ? (
                <>
                  <Button
                    variant={'outline'}
                    onClick={() => navigate('/homecare/payments')}
                    className="secondary-button"
                  >
                    {t('booking.requestStatus.retry')}
                  </Button>
                  <Button
                    variant={'default'}
                    onClick={() => navigate('/')}
                    className="primary-button bg-green-600 hover:bg-green-700"
                  >
                    {t('booking.requestStatus.home')}
                  </Button>
                </>
              ) : (
                <Button
                  variant={'default'}
                  onClick={() => navigate('/')}
                  className="primary-button bg-green-600 hover:bg-green-700"
                >
                  {t('booking.requestStatus.home')}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
}
